import {
  CharacteristicFilterType,
  CharacteristicFilterWithRange,
  EntityFilterType,
  StoreShortWithSupplierType,
} from '@/types'
import { hasUniformEligibleSlottedFilter } from '@/utils/search/characteristics/slottedCharacteristics'

import { FiltersState } from './filtersTypes'

const effectiveMatchUniform = (
  matchUniform: boolean | undefined,
  characteristics: CharacteristicFilterType[] | undefined
) => !!matchUniform && hasUniformEligibleSlottedFilter(characteristics ?? [])

export class FiltersStateService {
  private normalizeCharsForComparison(chars: CharacteristicFilterType[]) {
    if (!chars) return []
    return chars
      .map((c) => {
        const values = Array.from(
          new Set([
            ...(c.options?.map((o) => String(o.value)) || []),
            ...(c.chosenOptions || []),
          ])
        ).sort()

        return {
          code: c.code,
          values,
          range: c.chosenRange
            ? { min: c.chosenRange.min, max: c.chosenRange.max }
            : null,
        }
      })
      .sort((a, b) => a.code - b.code)
  }

  createInitialState(): FiltersState {
    return {
      searchWithLeftovers: false,
      selectedStores: [],
      selectedBrands: [],
      selectedCategories: [],
      selectedCharacteristics: [],
      selectedParents: [],
      searchWithDiscontinued: true,
    }
  }

  syncWithGlobalState(
    currentlySearchableBrands: EntityFilterType[],
    currentlySearchableCategories: EntityFilterType[],
    currentlySearchableStores: StoreShortWithSupplierType[],
    currentlySearchableCharacteristics: CharacteristicFilterWithRange[],
    currentlySearchableParents: EntityFilterType[],
    withLeftovers: boolean,
    withDiscontinued: boolean
  ): FiltersState {
    return {
      searchWithLeftovers: withLeftovers,
      selectedStores: currentlySearchableStores,
      selectedBrands: currentlySearchableBrands,
      selectedCategories: currentlySearchableCategories,
      selectedCharacteristics: currentlySearchableCharacteristics,
      selectedParents: currentlySearchableParents,
      searchWithDiscontinued: withDiscontinued,
    }
  }

  createFiltersStateString(state: FiltersState): string {
    return JSON.stringify({
      leftovers: state.searchWithLeftovers,
      discontinued: state.searchWithDiscontinued,
      stores: state.selectedStores.map((s) => s.id).sort(),
      brands: state.selectedBrands.map((b) => b.id).sort(),
      categories: state.selectedCategories.map((c) => c.id).sort(),
      parents: state.selectedParents.map((p) => p.id).sort(),
      chars: this.normalizeCharsForComparison(state.selectedCharacteristics),
      uniform: effectiveMatchUniform(
        state.searchMatchUniformSlotted,
        state.selectedCharacteristics
      ),
    })
  }

  createAppliedStateString(
    withLeftovers: boolean,
    withDiscontinued: boolean,
    stores: StoreShortWithSupplierType[],
    brands: EntityFilterType[],
    categories: EntityFilterType[],
    parents: EntityFilterType[],
    characteristics: CharacteristicFilterWithRange[],
    matchUniformSlottedCharacteristics = false
  ): string {
    return JSON.stringify({
      leftovers: withLeftovers,
      discontinued: withDiscontinued,
      stores: (stores || []).map((s) => s.id).sort(),
      brands: (brands || []).map((b) => b.id).sort(),
      categories: (categories || []).map((c) => c.id).sort(),
      parents: (parents || []).map((p) => p.id).sort(),
      chars: this.normalizeCharsForComparison(characteristics),
      uniform: effectiveMatchUniform(
        matchUniformSlottedCharacteristics,
        characteristics
      ),
    })
  }

  shouldUpdateDraftState(
    debouncedFiltersString: string,
    appliedStateString: string
  ): boolean {
    return debouncedFiltersString !== appliedStateString
  }

  resetFilters(): FiltersState {
    return this.createInitialState()
  }

  shouldEnrichCharacteristics(
    selectedCharacteristics: CharacteristicFilterType[],
    availableCharacteristics: CharacteristicFilterType[]
  ): boolean {
    if (!availableCharacteristics || !selectedCharacteristics.length) {
      return false
    }
    return selectedCharacteristics.every((char) => char.name === '')
  }

  enrichCharacteristics(
    selectedCharacteristics: CharacteristicFilterType[],
    availableCharacteristics: CharacteristicFilterType[]
  ): CharacteristicFilterType[] {
    if (
      !this.shouldEnrichCharacteristics(
        selectedCharacteristics,
        availableCharacteristics
      )
    ) {
      return selectedCharacteristics
    }

    return selectedCharacteristics
      .map((selectedCharacteristic) =>
        availableCharacteristics.filter(
          (availableFiltersCharacteristic) =>
            selectedCharacteristic.code === availableFiltersCharacteristic.code
        )
      )
      .flat()
  }
}

export const filtersStateService = new FiltersStateService()
